import { useEffect, useId, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, CameraOff } from "lucide-react";

const SCAN_COOLDOWN_MS = 1800;

function computeQrBoxSize(width, height) {
  const edge = Math.min(width || 320, height || 320);
  return Math.max(240, Math.min(480, Math.floor(edge * 0.72)));
}

/**
 * Device camera QR scanner for gate check-in desks.
 * Calls onScan with decoded text; debounces repeat reads of the same code.
 */
export default function QrCameraScanner({ active = true, onScan, className = "" }) {
  const domId = useId().replace(/:/g, "");
  const containerRef = useRef(null);
  const scannerRef = useRef(null);
  const lastScanRef = useRef({ code: "", at: 0 });
  const onScanRef = useRef(onScan);
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraError, setCameraError] = useState("");

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  useEffect(() => {
    if (!active) {
      setCameraOn(false);
      return undefined;
    }

    let cancelled = false;
    const scanner = new Html5Qrcode(domId, { verbose: false });
    scannerRef.current = scanner;

    const start = async () => {
      setCameraError("");
      const box = containerRef.current
        ? computeQrBoxSize(containerRef.current.clientWidth, containerRef.current.clientHeight)
        : qrBox;
      try {
        await scanner.start(
          { facingMode: "environment" },
          { fps: 12, qrbox: { width: box, height: box }, aspectRatio: 1 },
          (decoded) => {
            const code = String(decoded || "").trim();
            if (!code) return;
            const now = Date.now();
            const last = lastScanRef.current;
            if (code === last.code && now - last.at < SCAN_COOLDOWN_MS) return;
            lastScanRef.current = { code, at: now };
            onScanRef.current?.(code);
          },
          () => {
            /* frame decode miss — expected while aiming */
          }
        );
        if (!cancelled) setCameraOn(true);
      } catch (err) {
        if (!cancelled) {
          setCameraOn(false);
          setCameraError(err?.message || "Camera unavailable. Allow camera access or use manual input.");
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      const stopPromise = scanner.isScanning
        ? scanner.stop().then(() => scanner.clear()).catch(() => {})
        : Promise.resolve();
      stopPromise.finally(() => {
        scannerRef.current = null;
      });
    };
  }, [active, domId]);

  return (
    <div ref={containerRef} className={`relative w-full h-full min-h-[280px] ${className}`}>
      <div id={domId} className="absolute inset-0 [&_video]:object-cover [&_video]:w-full [&_video]:h-full [&_img]:hidden" />
      {!cameraOn && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center bg-black/80">
          {cameraError ? (
            <>
              <CameraOff size={48} className="text-[var(--error)]" strokeWidth={1.4} />
              <p className="text-sm font-mono text-[var(--text-secondary)] max-w-md">{cameraError}</p>
            </>
          ) : (
            <>
              <Camera size={48} className="text-[var(--primary)] animate-pulse" strokeWidth={1.4} />
              <p className="text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)]">Starting camera…</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
